import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge';
import { getFitTests } from '../../api/fitTests';
import { getEmpresas } from '../../api/empresas';
import { useAuth } from '../../contexts/AuthContext';
import { tipoIcon, TIPOS_TESTE } from '../../utils/fitTestOpcoes';
import { calcConformidade, CONFORMIDADE_CONFIG } from '../../utils/conformidade';
import { getFuncionarios } from '../../api/funcionarios';

function getPeriodoRange(periodo) {
  const now = new Date();
  if (periodo === 'hoje') {
    const ini = new Date(now); ini.setHours(0, 0, 0, 0);
    const fim = new Date(now); fim.setHours(23, 59, 59, 999);
    return [ini, fim];
  }
  if (periodo === 'semana') {
    const dom = new Date(now); dom.setHours(0, 0, 0, 0); dom.setDate(now.getDate() - now.getDay());
    const sab = new Date(dom); sab.setDate(dom.getDate() + 6); sab.setHours(23, 59, 59, 999);
    return [dom, sab];
  }
  if (periodo === 'mes') {
    return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)];
  }
  return [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999)];
}

const PERIODO_LABEL = { hoje: 'Hoje', semana: 'Semana', mes: 'Mês', ano: 'Ano' };

export default function Dashboard() {
  const { isFenix } = useAuth();
  const [tests, setTests] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('semana');
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [expandidas, setExpandidas] = useState({});
  const toggleEmpresa = (id) => setExpandidas(prev => ({ ...prev, [id]: !prev[id] }));
  const today = new Date();

  useEffect(() => {
    const reqs = [getFitTests(), getFuncionarios()];
    if (isFenix) reqs.push(getEmpresas());
    Promise.all(reqs)
      .then(([t, f, e]) => {
        setTests(t);
        setFuncionarios(f);
        if (e) setEmpresas(e);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isFenix]);

  const testsFiltrados = useMemo(() =>
    filtroEmpresa
      ? tests.filter(t => t.funcionario_empresa?.empresa?.id_empresa === filtroEmpresa)
      : tests,
    [tests, filtroEmpresa]
  );

  const vencidos  = testsFiltrados.filter(t => t.data_validade && new Date(t.data_validade) < today);
  const vence30   = testsFiltrados.filter(t => {
    if (!t.data_validade) return false;
    const diff = (new Date(t.data_validade) - today) / 86400000;
    return diff >= 0 && diff <= 30;
  });
  const aprovados = testsFiltrados.filter(t => t.resultado === 'APROVADO' && t.data_validade && new Date(t.data_validade) >= today);

  const [rangeIni, rangeFim] = getPeriodoRange(periodo);
  const agendadosPeriodo = testsFiltrados.filter(t => {
    if (t.situacao !== 'confirmado' || t.data_realizada || !t.data_agendada) return false;
    const d = new Date(t.data_agendada);
    return d >= rangeIni && d <= rangeFim;
  });

  // Breakdown por tipo de teste
  const porTipo = useMemo(() => {
    const map = {};
    testsFiltrados.forEach(t => { map[t.tipo_teste] = (map[t.tipo_teste] || 0) + 1; });
    return map;
  }, [testsFiltrados]);

  // Resumo por empresa → locais dentro de cada empresa
  const porEmpresa = useMemo(() => {
    const contagem = (map, key, t) => {
      if (!map[key]) map[key] = { total: 0, aprovados: 0, vence30: 0, vencidos: 0, pendentes: 0 };
      const g = map[key];
      g.total++;
      if (t.data_validade) {
        const d = new Date(t.data_validade);
        const diff = (d - today) / 86400000;
        if (d < today) g.vencidos++;
        else if (diff <= 30) g.vence30++;
      }
      if (t.resultado === 'APROVADO' && t.data_validade && new Date(t.data_validade) >= today) g.aprovados++;
      if (t.situacao === 'confirmado' && !t.data_realizada) g.pendentes++;
    };

    const empresaMap = {};
    testsFiltrados.forEach(t => {
      const empId   = t.funcionario_empresa?.empresa?.id_empresa || 'sem';
      const empNome = t.funcionario_empresa?.empresa?.nome_fantasia || t.funcionario_empresa?.empresa?.razao_social || 'Sem empresa';
      const local   = t.local?.nome || t.funcionario_empresa?.embarcacao || 'Sem local';

      if (!empresaMap[empId]) empresaMap[empId] = { id: empId, nome: empNome, locais: {}, total: 0, aprovados: 0, vence30: 0, vencidos: 0, pendentes: 0 };
      contagem(empresaMap, empId, t);
      contagem(empresaMap[empId].locais, local, t);
      if (!empresaMap[empId].locais[local].nome) empresaMap[empId].locais[local].nome = local;
    });

    return Object.values(empresaMap)
      .map(e => ({ ...e, locais: Object.values(e.locais).sort((a, b) => b.vencidos - a.vencidos) }))
      .sort((a, b) => b.vencidos - a.vencidos || b.vence30 - a.vence30);
  // eslint-disable-next-line
  }, [testsFiltrados]);

  const statCards = [
    { label: 'Testes vencidos',    value: vencidos.length,       accent: '#ef4444', bg: '#fef2f2', icon: 'bi-exclamation-circle' },
    { label: 'Vencem em 30 dias',  value: vence30.length,        accent: '#f59e0b', bg: '#fffbeb', icon: 'bi-clock-history' },
    { label: 'Aprovados vigentes', value: aprovados.length,      accent: '#10b981', bg: '#ecfdf5', icon: 'bi-check-circle' },
    { label: 'Total de registros', value: testsFiltrados.length, accent: 'var(--main-color)', bg: 'rgba(82,0,255,.06)', icon: 'bi-clipboard-data' },
  ];

  const upcoming = [...vencidos, ...vence30].slice(0, 8);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Visão geral dos fit tests e vencimentos" />

      {/* Filtro de empresa (fenix/admin) */}
      {isFenix && empresas.length > 0 && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="small text-muted">Filtrar por empresa:</span>
          <select className="form-select form-select-sm" style={{ maxWidth: 260 }}
            value={filtroEmpresa} onChange={e => setFiltroEmpresa(e.target.value)}>
            <option value="">Todas as empresas</option>
            {empresas.map(e => <option key={e.id_empresa} value={e.id_empresa}>{e.nome_fantasia || e.razao_social}</option>)}
          </select>
          {filtroEmpresa && (
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setFiltroEmpresa('')}>
              <i className="bi bi-x me-1"></i>Limpar
            </button>
          )}
        </div>
      )}

      {/* Cards principais */}
      <div className="row g-3 mb-3">
        {statCards.map(({ label, value, accent, bg, icon }) => (
          <div key={label} className="col-6 col-md-3">
            <div className="card-datafit p-3 h-100" style={{ borderLeft: `3px solid ${accent}` }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="fs-2 fw-bold" style={{ color: accent }}>{value}</div>
                  <div className="small text-muted mt-1">{label}</div>
                </div>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className={`bi ${icon} fs-5`} style={{ color: accent }}></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Conformidade da empresa — visível apenas para clientes */}
      {!isFenix && (() => {
        const totalFunc = funcionarios.length;
        const grupos = { em_dia: [], proximo: [], vencido: [], sem_teste: [] };
        funcionarios.forEach(f => {
          const va = f.vinculos?.find(v => v.ativo);
          const status = calcConformidade(va) || 'sem_teste';
          grupos[status]?.push(f);
        });
        const naoConformes = [...grupos.vencido, ...grupos.proximo, ...grupos.sem_teste];
        return (
          <div className="card-datafit mb-4">
            <div className="p-3 border-bottom d-flex align-items-center gap-2">
              <i className="bi bi-shield-check fs-5" style={{ color: 'var(--main-color)' }}></i>
              <span className="fw-bold" style={{ color: 'var(--main-color)', textTransform: 'uppercase', fontSize: '.85rem' }}>
                Conformidade da empresa
              </span>
            </div>
            <div className="p-3">
              <div className="row g-3 mb-3">
                {Object.entries(CONFORMIDADE_CONFIG).map(([key, cfg]) => (
                  <div key={key} className="col-6 col-md-3">
                    <div className="border rounded p-3 text-center h-100">
                      <i className={`bi ${cfg.icon} fs-3`} style={{ color: cfg.color }}></i>
                      <div className="fs-4 fw-bold mt-1" style={{ color: cfg.color }}>{grupos[key]?.length || 0}</div>
                      <div className="small text-muted">{cfg.label}</div>
                      <div className="small text-muted">{totalFunc > 0 ? Math.round((grupos[key]?.length || 0) * 100 / totalFunc) : 0}%</div>
                    </div>
                  </div>
                ))}
              </div>
              {naoConformes.length > 0 && (
                <>
                  <div className="small fw-bold text-muted text-uppercase mb-2" style={{ fontSize: '.7rem', letterSpacing: '.06em' }}>
                    Funcionários que precisam de atenção ({naoConformes.length})
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr><th>Funcionário</th><th>Situação</th><th>Resp. validade</th><th>Aud. validade</th><th></th></tr>
                      </thead>
                      <tbody>
                        {naoConformes.map(f => {
                          const va = f.vinculos?.find(v => v.ativo);
                          const status = calcConformidade(va) || 'sem_teste';
                          const cfg = CONFORMIDADE_CONFIG[status];
                          return (
                            <tr key={f.id_funcionario}>
                              <td className="fw-500">{f.nome}</td>
                              <td>
                                <i className={`bi ${cfg.icon} me-1`} style={{ color: cfg.color }}></i>
                                <span className="small" style={{ color: cfg.color }}>{cfg.label}</span>
                              </td>
                              <td className="small text-muted">{va?.ultima_validade_respiratorio || '—'}</td>
                              <td className="small text-muted">{va?.ultima_validade_auditivo || '—'}</td>
                              <td>
                                <Link to="/solicitar-agendamento" className="btn btn-sm btn-outline-primary" style={{ fontSize: '.75rem' }}>
                                  Agendar
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Breakdown por tipo de teste */}
      <div className="row g-3 mb-4">
        {TIPOS_TESTE.map(({ value, label, icon }) => (
          <div key={value} className="col-4">
            <div className="card-datafit p-3 d-flex align-items-center gap-3">
              <i className={`bi ${icon} fs-4 text-muted`}></i>
              <div>
                <div className="fw-bold">{porTipo[value] || 0}</div>
                <div className="small text-muted">{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Agendamentos a realizar */}
      <div className="card-datafit mb-4">
        <div className="p-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-calendar-check fs-5" style={{ color: 'var(--main-color)' }}></i>
            <span className="fw-bold" style={{ color: 'var(--main-color)', textTransform: 'uppercase', fontSize: '.85rem' }}>
              Agendamentos a realizar
            </span>
            <span className="badge rounded-pill"
              style={{ background: agendadosPeriodo.length > 0 ? 'var(--main-color)' : '#6c757d', color: '#fff', fontSize: '.8rem' }}>
              {agendadosPeriodo.length}
            </span>
          </div>
          <div className="d-flex gap-1">
            {Object.entries(PERIODO_LABEL).map(([key, label]) => (
              <button key={key}
                className={`btn btn-sm ${periodo === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={periodo === key ? { background: 'var(--main-color)', borderColor: 'var(--main-color)' } : {}}
                onClick={() => setPeriodo(key)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {agendadosPeriodo.length === 0 ? (
          <div className="p-4 text-center text-muted small">
            <i className="bi bi-calendar-x fs-4 d-block mb-2 opacity-50"></i>
            Nenhum agendamento confirmado para {PERIODO_LABEL[periodo].toLowerCase()}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-datafit mb-0">
              <thead>
                <tr><th>Funcionário</th><th>Empresa</th><th>Local</th><th>Tipo</th><th>Equipamento</th><th>Data agendada</th></tr>
              </thead>
              <tbody>
                {agendadosPeriodo.map(t => (
                  <tr key={t.id_fit_test}>
                    <td className="fw-500">{t.funcionario_empresa?.funcionario?.nome || '—'}</td>
                    <td className="small text-muted">{t.funcionario_empresa?.empresa?.nome_fantasia || t.funcionario_empresa?.empresa?.razao_social || '—'}</td>
                    <td className="small text-muted">{t.local?.nome || t.funcionario_empresa?.embarcacao || '—'}</td>
                    <td>
                      <i className={`bi ${tipoIcon(t.tipo_teste)} me-1`}></i>
                      {t.tipo_teste}
                    </td>
                    <td>
                      <div>{t.equipamento?.ca || '—'}</div>
                      <div className="small text-muted">{t.equipamento?.tipo_mascara}</div>
                    </td>
                    <td>{t.data_agendada}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alerta de vencimentos */}
      {(vencidos.length > 0 || vence30.length > 0) && (
        <div className="d-flex align-items-center gap-3 mb-4 p-3"
          style={{ background: 'rgba(82,0,255,.06)', border: '1px solid rgba(82,0,255,.15)', borderRadius: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9, flexShrink: 0,
            background: 'var(--gradient-diagonal)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="bi bi-exclamation-triangle-fill" style={{ color: '#fff', fontSize: '.95rem' }}></i>
          </div>
          <span style={{ color: 'var(--dark-color)', fontSize: '.9rem' }}>
            <strong style={{ color: '#ef4444' }}>{vencidos.length} teste(s)</strong> vencidos e{' '}
            <strong style={{ color: '#f59e0b' }}>{vence30.length} teste(s)</strong> vencem nos próximos 30 dias.
          </span>
        </div>
      )}

      {/* Resumo por embarcação */}
      <div className="card-datafit mb-4">
        <div className="p-3 border-bottom d-flex align-items-center gap-2">
          <i className="bi bi-building fs-5" style={{ color: 'var(--main-color)' }}></i>
          <span className="fw-bold" style={{ color: 'var(--main-color)', textTransform: 'uppercase', fontSize: '.85rem' }}>
            Situação por empresa
          </span>
          <span className="small text-muted fw-normal">— clique em uma empresa para ver os locais</span>
        </div>
        {porEmpresa.length === 0 ? (
          <div className="p-4 text-center text-muted small">Nenhum dado disponível</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-datafit mb-0">
              <thead>
                <tr>
                  <th>Empresa / Local</th>
                  <th className="text-center">Total</th>
                  <th className="text-center">Aprovados</th>
                  <th className="text-center">Próx. venc.</th>
                  <th className="text-center">Vencidos</th>
                  <th className="text-center">Pendentes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {porEmpresa.map(emp => (
                  <React.Fragment key={emp.id}>
                    <tr
                      style={{ cursor: 'pointer', background: expandidas[emp.id] ? 'rgba(82,0,255,.03)' : undefined }}
                      onClick={() => toggleEmpresa(emp.id)}
                    >
                      <td className="fw-bold">
                        <i className={`bi bi-chevron-${expandidas[emp.id] ? 'down' : 'right'} me-2 small text-muted`}></i>
                        {emp.nome}
                      </td>
                      <td className="text-center">
                        <span className="badge rounded-pill" style={{ background: 'rgba(82,0,255,.1)', color: 'var(--main-color)', fontWeight: 600 }}>{emp.total}</span>
                      </td>
                      <td className="text-center">
                        {emp.aprovados > 0 ? <span className="badge rounded-pill" style={{ background: '#d1fae5', color: '#065f46', fontWeight: 600 }}>{emp.aprovados}</span> : <span className="text-muted small">—</span>}
                      </td>
                      <td className="text-center">
                        {emp.vence30 > 0 ? <span className="badge rounded-pill" style={{ background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>{emp.vence30}</span> : <span className="text-muted small">—</span>}
                      </td>
                      <td className="text-center">
                        {emp.vencidos > 0 ? <span className="badge rounded-pill" style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 600 }}>{emp.vencidos}</span> : <span className="text-muted small">—</span>}
                      </td>
                      <td className="text-center text-muted small">{emp.pendentes || '—'}</td>
                      <td></td>
                    </tr>
                    {expandidas[emp.id] && emp.locais.map((loc, i) => (
                      <tr key={i} style={{ background: 'rgba(82,0,255,.02)' }}>
                        <td className="ps-5 small text-muted">
                          <i className="bi bi-geo-alt me-1"></i>{loc.nome}
                        </td>
                        <td className="text-center small text-muted">{loc.total}</td>
                        <td className="text-center small">
                          {loc.aprovados > 0 ? <span style={{ color: '#065f46', fontWeight: 600 }}>{loc.aprovados}</span> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center small">
                          {loc.vence30 > 0 ? <span style={{ color: '#92400e', fontWeight: 600 }}>{loc.vence30}</span> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center small">
                          {loc.vencidos > 0 ? <span style={{ color: '#991b1b', fontWeight: 600 }}>{loc.vencidos}</span> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center small text-muted">{loc.pendentes || '—'}</td>
                        <td></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Próximos vencimentos */}
      <div className="card-datafit">
        <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
          <span className="fw-bold" style={{ color: 'var(--main-color)', textTransform: 'uppercase', fontSize: '.85rem' }}>
            Próximos vencimentos
          </span>
          <Link to="/fit-tests" className="small" style={{ color: 'var(--main-color)' }}>
            Ver todos <i className="bi bi-arrow-right"></i>
          </Link>
        </div>
        <div className="table-responsive">
          <table className="table table-datafit mb-0">
            <thead>
              <tr><th>Funcionário</th><th>Tipo</th><th>Equipamento</th><th>Validade</th><th>Status</th></tr>
            </thead>
            <tbody>
              {upcoming.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted py-4">Nenhum vencimento próximo</td></tr>
              ) : upcoming.map(t => (
                <tr key={t.id_fit_test}>
                  <td className="fw-500">{t.funcionario_empresa?.funcionario?.nome || '—'}</td>
                  <td>
                    <i className={`bi ${tipoIcon(t.tipo_teste)} me-1`}></i>{t.tipo_teste}
                  </td>
                  <td>{t.equipamento?.ca || '—'}</td>
                  <td>{t.data_validade || '—'}</td>
                  <td><StatusBadge status={t.resultado} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
